import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ITeamRepository,
  TenantMember,
  PermissionAction,
} from '@erp/data-access/interfaces';

/** Row shape returned by RPC list_tenant_members_with_emails. */
interface MemberRow {
  user_id: string;
  tenant_id: string;
  email: string;
  role_id: string;
  role_slug: string;
  role_name: string;
  is_system: boolean;
  created_at: string;
  landing_route: string | null;
  kiosk: boolean;
  kiosk_allowed_paths: string[] | null;
  max_discount_percent: number | null;
}

interface PermissionRow {
  role_id: string;
  module: string;
  action: PermissionAction;
}

/** Wraps PostgrestError into a real Error so consumer `instanceof Error`
 *  checks match. Mirror of the helpers in roles/invitations repos. */
function rpcError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const e = err as { message?: string; code?: string; details?: string; hint?: string; status?: number; name?: string };
    const wrapped = new Error(e.message ?? 'rpc_failed');
    if (e.code) (wrapped as Error & { code?: string }).code = e.code;
    if (e.details) (wrapped as Error & { details?: string }).details = e.details;
    if (e.hint) (wrapped as Error & { hint?: string }).hint = e.hint;
    if (typeof e.status === 'number') (wrapped as Error & { status?: number }).status = e.status;
    if (e.name) wrapped.name = e.name;
    return wrapped;
  }
  return new Error(String(err));
}

/** Tenant member operations. Uses the `list_tenant_members_with_emails`
 *  SECURITY DEFINER RPC because auth.users isn't exposed via PostgREST. */
export class SupabaseTeamRepository implements ITeamRepository {
  constructor(private supabase: SupabaseClient) {}

  async list(): Promise<TenantMember[]> {
    const { data, error } = await this.supabase.rpc(
      'list_tenant_members_with_emails',
      {},
    );
    if (error) throw rpcError(error);
    return ((data ?? []) as MemberRow[]).map(mapMember);
  }

  /** Caller's own member row, with full permission list pre-joined. Used by
   *  usePermissions hook on every page load. Returns null if the caller has
   *  no membership (e.g. just signed up, hasn't accepted an invite, etc.). */
  async getCurrentMember(): Promise<TenantMember | null> {
    type SbAny = {
      auth: {
        getUser: () => Promise<{
          data: { user: { id: string } | null };
          error: unknown;
        }>;
      };
    };
    const { data: u, error: userError } = await (this.supabase as unknown as SbAny).auth.getUser();
    // A failed online identity check is not an authoritative absence of
    // membership. Throw so the scoped query retains its last known member.
    if (userError) throw rpcError(userError);
    const userId = u?.user?.id;
    if (!userId) return null;

    const { data, error } = await this.supabase.rpc(
      'list_tenant_members_with_emails',
      {},
    );
    if (error) throw rpcError(error);
    const row = ((data ?? []) as MemberRow[]).find((m) => m.user_id === userId);
    if (!row) return null;

    // Fetch permissions for this role.
    const { data: perms, error: perr } = await this.supabase
      .from('role_permissions')
      .select('module, action')
      .eq('role_id', row.role_id);
    if (perr) throw rpcError(perr);
    const permissions = ((perms ?? []) as PermissionRow[]).map((p) => ({
      module: p.module,
      action: p.action,
    }));
    return { ...mapMember(row), permissions };
  }

  async updateRole(userId: string, roleId: string): Promise<TenantMember> {
    const { error } = await this.supabase.rpc('update_tenant_member_role', {
      p_user_id: userId,
      p_role_id: roleId,
    });
    if (error) throw rpcError(error);
    // Re-fetch to get the joined role name/slug for the returned object.
    const all = await this.list();
    const updated = all.find((m) => m.userId === userId);
    if (!updated) throw new Error('member_not_found_after_update');
    return updated;
  }

  async remove(userId: string): Promise<void> {
    const { error } = await this.supabase.rpc('remove_tenant_member', {
      p_user_id: userId,
    });
    if (error) throw rpcError(error);
  }
}

function mapMember(r: MemberRow): TenantMember {
  return {
    userId: r.user_id,
    tenantId: r.tenant_id,
    email: r.email,
    roleId: r.role_id,
    roleSlug: r.role_slug,
    roleName: r.role_name,
    isOwner: r.role_slug === 'owner',
    isSystemRole: r.is_system,
    createdAt: r.created_at,
    // Défauts défensifs : si le RPC n'a pas encore été migré (déploiement
    // en deux temps), le kiosque reste simplement inactif.
    landingRoute: r.landing_route ?? null,
    kiosk: r.kiosk === true,
    kioskAllowedPaths: r.kiosk_allowed_paths ?? [],
    maxDiscountPercent: Number(r.max_discount_percent) || 0,
  };
}
